import React, { useState, useEffect, useCallback, useRef } from 'react'; // Add useRef
import { Container, Row, Col, Card, Table, Spinner, Alert, Badge, Button, Accordion, Tabs, Tab, Dropdown, ListGroup } from 'react-bootstrap'; // Add Dropdown, ListGroup
import { useParams, useNavigate } from 'react-router-dom';
import { agentApi, notificationApi, searchApi } from '../../services/api'; // Import notificationApi, searchApi
import {
  FaArrowLeft,
  FaIdCard,
  FaPhone,
  FaEnvelope,
  FaCalendarAlt,
  FaUniversity,
  FaCreditCard,
  FaUserCircle, // Keep needed icons
  FaSignOutAlt, // Keep needed icons
  FaSearch, FaTimes, FaQuestionCircle, FaBell // Add necessary icons
} from 'react-icons/fa';
// import { Link } from 'react-router-dom'; // Sidebar is now a component
import { useAuth } from '../../hooks/useAuth';
import AgentSidebar from '../../components/AgentSidebar'; // Import AgentSidebar
import SearchResults from '../../components/SearchResults'; // Import SearchResults
import './Settings.css'; // Import the shared CSS

const CibilDetails = () => {
  const { cibilId } = useParams();
  const navigate = useNavigate();
  const { getCurrentUser, logout } = useAuth();
  const currentUser = getCurrentUser();

  // --- Start: Added State/Refs from Settings.js ---
  // --- Search State ---
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  // const [showSearchModal, setShowSearchModal] = useState(false); // Not needed if modal isn't used here
  const searchRef = useRef(null);
  // --- End Search State ---

  // --- Notification State ---
  const [notifications, setNotifications] = useState([]);
  // const [notificationsEnabled, setNotificationsEnabled] = useState(true); // Not needed for display
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false); // Controls dropdown visibility
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const notificationRef = useRef(null);

  // Notification handlers (Copy these functions exactly from AgentSettings.js)
  const toggleNotifications = () => {
    setShowNotifications(prev => !prev);
  };

  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification || notification.is_read) return;

    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;

    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // formatTimestamp function (Copy exactly from AgentSettings.js)
  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) { return timestamp; }
  };

  // Search handler functions (Copy these functions exactly from AgentSettings.js)
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (query.trim().length >= 2) {
      const timeout = setTimeout(() => {
        performSearch(query);
      }, 500);
      setSearchTimeout(timeout);
    } else {
      setShowSearchResults(false);
      setSearchResults([]);
    }
  };

  const performSearch = async (query) => {
    if (!query.trim() || query.length < 2) return;

    setIsSearching(true);
    try {
      const response = await searchApi.globalSearch(query, { limit_per_type: 5 });
      setSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  // Logout handler (Copy exactly from AgentSettings.js)
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  // --- End: Added State/Refs/Handlers from Settings.js ---

  // CIBIL data state (Keep existing CIBIL state)
  const [cibilData, setCibilData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Use useCallback to memoize the fetchCibilData function
  const fetchCibilData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Call the API to fetch CIBIL data
      const response = await agentApi.getCibilData(cibilId);
      setCibilData(response.data);
    } catch (err) {
      console.error("Error fetching CIBIL data:", err);
      setError(err.response && err.response.data && err.response.data.detail ? 
               err.response.data.detail : 'Failed to load CIBIL data.');
    } finally {
      setLoading(false);
    }
  }, [cibilId]);

  useEffect(() => {
    document.title = 'CIBIL Details';
    fetchCibilData();
  }, [fetchCibilData]);

  // --- Start: Added useEffects from Settings.js ---
  // Notifications polling effect
  useEffect(() => {
    let intervalId;
    const fetchNotificationsData = async () => {
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
    };

    fetchNotificationsData(); // Initial fetch
    intervalId = setInterval(fetchNotificationsData, 60000); // Poll count every minute

    return () => clearInterval(intervalId); // Cleanup
  }, []);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    const fetchFullNotifications = async () => {
      if (showNotifications) {
        setLoadingNotifications(true);
        try {
          const notificationsResponse = await notificationApi.getNotifications({ limit: 10 });
          setNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications:', error);
          setNotifications([]);
        } finally {
          setLoadingNotifications(false);
        }
      }
    };
    fetchFullNotifications();
  }, [showNotifications]);

  // Handle clicks outside notification dropdown and search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // --- End: Added useEffects from Settings.js ---

  // Remove the old handleLogout function as it's added above
  // const handleLogout = () => { ... }; // REMOVE

  // Helper function to get color based on CIBIL score
  const getCibilScoreColor = (score) => {
    if (!score) return 'secondary';
    if (score >= 750) return 'success';
    if (score >= 650) return 'info';
    if (score >= 550) return 'warning';
    return 'danger';
  };

  // Helper function to format dates for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Helper function to get account type name
  const getAccountTypeName = (code) => {
    const accountTypes = {
      '01': 'Auto Loan (Personal)',
      '02': 'Housing Loan',
      '03': 'Property Loan',
      '04': 'Loan Against Shares/Securities',
      '05': 'Personal Loan',
      '06': 'Consumer Loan',
      '07': 'Gold Loan',
      '08': 'Education Loan',
      '09': 'Loan to Professional',
      '10': 'Credit Card',
      '11': 'Leasing',
      '12': 'Overdraft',
      '13': 'Two-wheeler Loan',
      '14': 'Non-Funded Credit Facility',
      '15': 'Corporate Credit Card',
      '16': 'Kisan Credit Card',
      '17': 'Loan on Credit Card',
      '18': 'Prime Minister Jaan Dhan Yojana - Overdraft',
      '19': 'Mudra Loans - Shishu/Kishor/Tarun',
      '20': 'Microfinance - Business Loan',
      '21': 'Microfinance - Personal Loan',
      '22': 'Microfinance - Housing Loan',
      '23': 'Microfinance - Other',
      '24': 'Business Loan - Secured',
      '25': 'Business Loan - Unsecured',
      '26': 'Business Non-Funded Credit Facility',
      '27': 'Business Loan Against Bank Deposits',
      '28': 'Telco - Wireless',
      '29': 'Telco - Broadband',
      '30': 'Telco - Landline',
      '31': 'Secured Credit Card',
      '32': 'Fleet Card',
      '33': 'Commercial Vehicle Loan',
      '34': 'Construction Equipment Loan',
      '35': 'Tractor Loan',
      '36': 'Auto Loan (Commercial)',
      '38': 'Loan Against Card Receivables',
      '39': 'Loan Against Bank Deposits',
      '40': 'Pradhan Mantri Awas Yojana - Credit Linked Subsidy Scheme MAY CLSS',
      '41': 'Medical Equipment Loan',
      '42': 'Commercial Real Estate Loan',
      '43': 'Used Car Loan',
      '51': 'P2P - Personal Loan',
      '53': 'Consumer Durable Loan',
      '61': 'Used Two-Wheeler Loan',
      '69': 'Buy Now Pay Later',
      '99': 'Other'
    };
    
    return accountTypes[code] || `Unknown Type (${code})`;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading CIBIL Data...</span>
          </Spinner>
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="danger">
          {error}
        </Alert>
      );
    }

    if (!cibilData) {
      return (
        <Alert variant="info">
          No CIBIL information found for this ID.
        </Alert>
      );
    }

    // Get personal info data safely
    const personalInfo = cibilData.personal_info || {};
    const contactInfo = cibilData.contact_info || {};
    const loansInfo = cibilData.loans_info || [];
    const cardsInfo = cibilData.cards_info || [];
    const inquiryInfo = cibilData.inquiry_info || [];
    const accountInfo = cibilData.account_info || [];
    
    // Get address safely
    const addresses = contactInfo.address && Array.isArray(contactInfo.address) ?
                     contactInfo.address : [];

    return (
      // Remove the outer React.Fragment
      // <React.Fragment>
        <>
        {/* Move the Back button inside the main content area */}
        <Button
          variant="link"
          className="mb-3 text-decoration-none"
          onClick={() => navigate(-1)} // Use navigate(-1) to go back
        >
          <FaArrowLeft className="me-2" /> Back
        </Button>

        <Row>
          <Col md={4}>
            <Card className="mb-4">
              <Card.Body>
                <div className="text-center mb-3">
                  <div className="avatar-circle-lg mx-auto mb-3">
                    {personalInfo.name ? personalInfo.name.charAt(0).toUpperCase() : '?'}
                  </div>
                  <h4>{personalInfo.name || 'N/A'}</h4>
                  <div className="d-flex justify-content-center">
                    <Badge 
                      bg={getCibilScoreColor(personalInfo.cibil_score)} 
                      className="px-3 py-2 fs-6"
                    >
                      CIBIL Score: {personalInfo.cibil_score || 'N/A'}
                    </Badge>
                  </div>
                </div>
                
                <hr />
                
                <h5 className="mb-3">Personal Information</h5>
                <Table borderless size="sm">
                  <tbody>
                    <tr>
                      <td width="40%"><FaIdCard className="me-2 text-primary" /> PAN</td>
                      <td>{personalInfo.pan || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><FaCalendarAlt className="me-2 text-primary" /> Date of Birth</td>
                      <td>{formatDate(personalInfo.dob)}</td>
                    </tr>
                    <tr>
                      <td><FaUserCircle className="me-2 text-primary" /> Gender</td>
                      <td>{personalInfo.gender || 'N/A'}</td>
                    </tr>
                  </tbody>
                </Table>
                
                <hr />
                
                <h5 className="mb-3">Contact Information</h5>
                <Table borderless size="sm">
                  <tbody>
                    <tr>
                      <td width="40%"><FaEnvelope className="me-2 text-primary" /> Email</td>
                      <td>{contactInfo.email || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><FaPhone className="me-2 text-primary" /> Phone</td>
                      <td>{contactInfo.phone || 'N/A'}</td>
                    </tr>
                  </tbody>
                </Table>
                
                {addresses.length > 0 && (
                  <React.Fragment>
                    <h6 className="mb-2 mt-3">Address</h6>
                    {addresses.map((addr, index) => (
                      <div key={index} className="mb-2 small">
                        {addr.street_address}, {addr.region_name || addr.region} - {addr.postal_code}<br />
                        <span className="text-muted">Reported: {formatDate(addr.date_reported)}</span>
                      </div>
                    ))}
                  </React.Fragment>
                )}
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={8}>
            <Tabs defaultActiveKey="loans" className="mb-3">
              <Tab eventKey="loans" title="Loans">
                {loansInfo.length > 0 ? (
                  loansInfo.map((loan, index) => (
                    <Card key={index} className="mb-3">
                      <Card.Header className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                          <FaUniversity className="me-2" /> 
                          {loan.bank_name || 'Unknown Bank'}
                        </h5>
                        <Badge bg="primary">{getAccountTypeName(loan.account_type)}</Badge>
                      </Card.Header>
                      <Card.Body>
                        <Row className="g-3">
                          <Col md={6}>
                            <div className="mb-3">
                              <small className="text-muted d-block">Sanctioned Amount</small>
                              <span className="fs-5">₹{loan.sanctioned_amount || 'N/A'}</span>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <small className="text-muted d-block">Current Balance</small>
                              <span className="fs-5">₹{loan.current_balance || '0'}</span>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="mb-3">
                              <small className="text-muted d-block">EMI</small>
                              {loan.emi && loan.emi !== "-1" ? (
                                <span>₹{loan.emi}</span>
                              ) : (
                                <span>N/A</span>
                              )}
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="mb-3">
                              <small className="text-muted d-block">ROI</small>
                              {loan.roi && loan.roi !== "-1.00" ? (
                                <span>{loan.roi}%</span>
                              ) : (
                                <span>N/A</span>
                              )}
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="mb-3">
                              <small className="text-muted d-block">Tenure</small>
                              {loan.tenure && loan.tenure !== "-1" ? (
                                <span>{loan.tenure} months</span>
                              ) : (
                                <span>N/A</span>
                              )}
                            </div>
                          </Col>
                        </Row>
                        <hr />
                        <Row className="g-3">
                          <Col md={6}>
                            <div className="mb-1">
                              <small className="text-muted d-block">Opened On</small>
                              <span>{formatDate(loan.date_opened)}</span>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-1">
                              <small className="text-muted d-block">Closed On</small>
                              {loan.date_closed ? (
                                <span>{formatDate(loan.date_closed)}</span>
                              ) : (
                                <Badge bg="success">Active</Badge>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  <Alert variant="info">
                    No loan information available.
                  </Alert>
                )}
              </Tab>
              
              <Tab eventKey="cards" title="Credit Cards">
                {cardsInfo.length > 0 ? (
                  cardsInfo.map((card, index) => (
                    <Card key={index} className="mb-3">
                      <Card.Header>
                        <h5 className="mb-0">
                          <FaCreditCard className="me-2" /> 
                          {card.bank_name || 'Unknown Bank'}
                        </h5>
                      </Card.Header>
                      <Card.Body>
                        <Row className="g-3">
                          <Col md={6}>
                            <div className="mb-3">
                              <small className="text-muted d-block">Credit Limit</small>
                              <span className="fs-5">₹{card.credit_limit || 'N/A'}</span>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <small className="text-muted d-block">Current Balance</small>
                              <span className="fs-5">₹{card.current_balance || '0'}</span>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-1">
                              <small className="text-muted d-block">Opened On</small>
                              <span>{formatDate(card.date_opened)}</span>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-1">
                              <small className="text-muted d-block">Closed On</small>
                              {card.date_closed ? (
                                <span>{formatDate(card.date_closed)}</span>
                              ) : (
                                <Badge bg="success">Active</Badge>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  <Alert variant="info">
                    No credit card information available.
                  </Alert>
                )}
              </Tab>
              
              <Tab eventKey="inquiries" title="Recent Inquiries">
                {inquiryInfo.length > 0 ? (
                  <Table responsive striped hover>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Lender</th>
                        <th>Type</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inquiryInfo.map((inquiry, index) => (
                        <tr key={index}>
                          <td>{formatDate(inquiry.inquiry_date)}</td>
                          <td>{inquiry.subscriber_name}</td>
                          <td>{getAccountTypeName(inquiry.inquiry_type)}</td>
                          <td>{inquiry.amount ? `₹${inquiry.amount}` : 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <Alert variant="info">
                    No inquiry information available.
                  </Alert>
                )}
              </Tab>
              
              <Tab eventKey="accounts" title="Detailed Accounts">
                {accountInfo.length > 0 ? (
                  <Accordion>
                    {accountInfo.map((account, index) => {
                      const accountDetails = account.account_details || {};
                      const dateDetails = account.date_details || {};
                      
                      return (
                        <Accordion.Item key={index} eventKey={index.toString()}>
                          <Accordion.Header>
                            <div className="d-flex justify-content-between w-100 me-3">
                              <span>
                                <FaUniversity className="me-2" />
                                {accountDetails.bank_name || 'Unknown Bank'} - 
                                {accountDetails.account_number || 'Unknown Account'}
                              </span>
                              <Badge bg="primary">
                                {getAccountTypeName(accountDetails.account_type)}
                              </Badge>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            <Row>
                              <Col md={6}>
                                <h6 className="mb-3">Account Details</h6>
                                <Table borderless size="sm">
                                  <tbody>
                                    <tr>
                                      <td width="40%">Credit Limit</td>
                                      <td>
                                        {accountDetails.credit_limit && 
                                         accountDetails.credit_limit !== "-1" ? 
                                        `₹${accountDetails.credit_limit}` : 'N/A'}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Current Balance</td>
                                      <td>
                                        {accountDetails.current_balance ? 
                                        `₹${accountDetails.current_balance}` : '₹0'}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Sanctioned Amount</td>
                                      <td>
                                        {accountDetails.sanctioned_amount ? 
                                        `₹${accountDetails.sanctioned_amount}` : 'N/A'}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>EMI Amount</td>
                                      <td>
                                        {accountDetails.emi_amount && 
                                         accountDetails.emi_amount !== "-1" ? 
                                        `₹${accountDetails.emi_amount}` : 'N/A'}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Interest Rate</td>
                                      <td>
                                        {accountDetails.interest_rate && 
                                         accountDetails.interest_rate !== "-1.00" ? 
                                        `${accountDetails.interest_rate}%` : 'N/A'}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Tenure</td>
                                      <td>
                                        {accountDetails.repayment_tenure && 
                                         accountDetails.repayment_tenure !== "-1" ? 
                                        `${accountDetails.repayment_tenure} months` : 'N/A'}
                                      </td>
                                    </tr>
                                  </tbody>
                                </Table>
                              </Col>
                              <Col md={6}>
                                <h6 className="mb-3">Date Information</h6>
                                <Table borderless size="sm">
                                  <tbody>
                                    <tr>
                                      <td width="40%">Opened On</td>
                                      <td>{formatDate(dateDetails.date_opened)}</td>
                                    </tr>
                                    <tr>
                                      <td>Closed On</td>
                                      <td>
                                        {dateDetails.date_closed ? 
                                        formatDate(dateDetails.date_closed) : 
                                        <Badge bg="success">Active</Badge>}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Last Payment</td>
                                      <td>{formatDate(dateDetails.date_last_payment)}</td>
                                    </tr>
                                    <tr>
                                      <td>Reported On</td>
                                      <td>{formatDate(dateDetails.date_reported)}</td>
                                    </tr>
                                  </tbody>
                                </Table>
                              </Col>
                            </Row>
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                ) : (
                  <Alert variant="info">
                    No detailed account information available.
                  </Alert>
                )}
              </Tab>
            </Tabs>
          </Col>
        </Row>
        </> // End the inner fragment
      // </React.Fragment> // Remove the outer React.Fragment
    );
  };

  return (
    // Use the layout from AgentSettings.js
    <div className="agent-dashboard-layout">
      <AgentSidebar />

      <div className="agent-content-wrapper">
        {/* Copy the header structure from AgentSettings.js */}
        <header className="agent-header">
          <div className="agent-greeting">
            <span className="agent-greeting-text">Hi, {currentUser?.name || 'Agent'} <span className="agent-greeting-emoji">👋</span></span>
          </div>

          <div ref={searchRef} className="agent-header-search">
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search leads, products, invoices..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowSearchResults(true);
                  }
                }}
              />
              {searchQuery && (
                <button className="clear-search-btn" onClick={clearSearch}>
                  <FaTimes />
                </button>
              )}
              {isSearching && (
                <Spinner
                  animation="border"
                  size="sm"
                  className="search-spinner"
                />
              )}
            </div>

            {/* Search results dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-dropdown">
                <SearchResults
                  results={searchResults}
                  onResultClick={() => setShowSearchResults(false)}
                />
              </div>
            )}
          </div>

          <div className="agent-header-actions">
            <div className="agent-header-icon">
              <FaQuestionCircle size={18} />
            </div>

            {/* Notifications Dropdown (Copy exactly from AgentSettings.js) */}
            <div ref={notificationRef} className="position-relative agent-notifications me-3">
              <Button
                variant="link"
                className="text-secondary p-0 position-relative"
                onClick={toggleNotifications}
                id="dropdown-notifications-agent-cibil" // Ensure unique ID if needed
                aria-expanded={showNotifications}
                title="Notifications"
              >
                <FaBell size={20} />
                {unreadCount > 0 && (
                  <Badge
                    pill
                    bg="danger"
                    className="position-absolute top-0 start-100 translate-middle border border-light"
                    style={{ fontSize: '0.65em', padding: '0.3em 0.5em' }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                    <span className="visually-hidden">unread notifications</span>
                  </Badge>
                )}
              </Button>

              <Dropdown.Menu
                show={showNotifications}
                className="shadow mt-2 notification-dropdown"
                style={{ minWidth: '350px', maxHeight: '450px', overflowY: 'auto' }}
              >
                <Dropdown.Header className="d-flex justify-content-between align-items-center">
                  Notifications
                  {unreadCount > 0 && (
                    <Button variant="link" size="sm" onClick={handleMarkAllAsRead} className="p-0 text-decoration-none">
                      Mark all read
                    </Button>
                  )}
                </Dropdown.Header>
                <Dropdown.Divider className="my-1" />
                {loadingNotifications ? (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" variant="primary" /> Loading...
                  </div>
                ) : notifications.length > 0 ? (
                  <ListGroup variant="flush">
                    {notifications.map(notif => (
                      <ListGroup.Item
                        key={notif.id}
                        action={!notif.is_read}
                        onClick={() => handleMarkAsRead(notif.id)}
                        style={{
                          cursor: !notif.is_read ? 'pointer' : 'default',
                          backgroundColor: !notif.is_read ? '#f8f9fa' : 'transparent',
                          borderLeft: !notif.is_read ? '4px solid #0d6efd' : 'none',
                          padding: '0.75rem 1.25rem',
                          borderBottom: '1px solid #eee'
                        }}
                        className="notification-item"
                      >
                        <div className={`fw-bold ${!notif.is_read ? 'text-primary' : ''}`}>{notif.title || 'Notification'}</div>
                        <div className="text-muted small mb-1">{notif.message || 'No details available.'}</div>
                        <small className="text-muted">{formatTimestamp(notif.created_at)}</small>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <Dropdown.ItemText className="text-center text-muted p-3">
                    You're all caught up!
                  </Dropdown.ItemText>
                )}
              </Dropdown.Menu>
            </div>
            {/* End Notifications Dropdown */}

            {/* User Profile Section (Copy exactly from AgentSettings.js) */}
            <div className="agent-user-profile">
              <div className="agent-avatar">
                <FaUserCircle /> {/* Use FaUserCircle or FaUser */}
              </div>
              <div className="agent-user-info">
                <span className="agent-user-name">
                  {currentUser?.name || 'Agent User'}
                </span>
                <button className="agent-logout-btn" onClick={handleLogout}>
                  <FaSignOutAlt size={14} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
            {/* End User Profile Section */}
          </div>
        </header>

        {/* Main Content Area for CIBIL Details */}
        <Container fluid className="p-4">
          <h2 className="page-title mb-4">CIBIL Report Details</h2>
          {/* Render the CIBIL specific content */}
          {renderContent()}
        </Container>
        {/* End Main Content Area */}

      </div>
      {/* End agent-content-wrapper */}

      {/* Remove the <style jsx> block entirely */}
      {/* <style jsx>{` ... `}</style> */}
    </div>
    // End agent-dashboard-layout
  );
};

export default CibilDetails;
